import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";

import GenderBlock from "./components/GenderBlock";
import UserInfoBlock from "./components/UserInfoBlock";
import MeasuredBy from "../molecules/MeasuredBy";

import { GENDERS, MODAL_STEPS } from "../../constants/modal";
import {
  activeStylesContinue,
  disabledStylesContinue,
  uxGender,
  welcomeScreenUI,
  policityStyles,
} from "../../configs";
import CustomCheckbox from "../ConsentScreen/components/CustomCheckbox";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import { findBrandByDomain } from "../../configs/configLoader";
import { handleAnalytics } from "../../utils/tracking";
import { hoverStylesContinue } from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import DescriptionBlock from "./components/DescriptionBlock";

import "./index.css";

interface StepIntroDesktopProps {
  step: {
    number: number;
  } | null;
  error: any;
  height: string;
  unit: string;
  feet: string;
  inches: string;
  unitWeight: string;
  weight?: string | undefined;
  age: string;
  selectedGender: string;
  disableContinue: boolean;
  handleFieldChange: (e: any, type: string, blurSignal: string) => void;
  handleUnitChange: (newUnit: string) => void;
  handleUnitWeightChange: (newUnit: string) => void;
  handleGenderSelect: (newUnit: string) => void;
  setNeedValidate: React.Dispatch<React.SetStateAction<boolean>>;
  nextStepGender: () => void;
  nextStep: () => void;
  titlesStyles: React.CSSProperties;
  descriptionStyles: React.CSSProperties;
  ageChildrenYears: string;
  ageChildrenMonths: string;
  unitAge: string;
  handleUnitAgeChange: (newUnit: string) => void;
  policyChecked: boolean;
  setPolicyChecked: React.Dispatch<React.SetStateAction<boolean>>;
  company: string;
  setTouchedCompany: React.Dispatch<React.SetStateAction<boolean>>;
  setFeetTouched: React.Dispatch<React.SetStateAction<boolean>>;
  setInchesTouched: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepIntroDesktop: React.FC<StepIntroDesktopProps> = ({
  step,
  error,
  height,
  unit,
  feet,
  inches,
  unitWeight,
  weight,
  age,
  selectedGender,
  disableContinue,
  handleFieldChange,
  handleUnitChange,
  handleUnitWeightChange,
  handleGenderSelect,
  setNeedValidate,
  nextStepGender,
  nextStep,
  titlesStyles,
  descriptionStyles,
  ageChildrenYears,
  ageChildrenMonths,
  unitAge,
  handleUnitAgeChange,
  policyChecked,
  setPolicyChecked,
  company,
  setTouchedCompany,
  setFeetTouched,
  setInchesTouched,
}) => {
  const isMobile = useIsMobile();

  const isMediumHeightRelative = useMediaQuery({
    maxHeight: 678,
    minWidth: 767,
  });

  const isSmallHeightRelative = useMediaQuery({
    maxHeight: 620,
    minWidth: 767,
  });

  const { t } = useTranslation("components/intro");

  const urlParameters = new URLSearchParams(window.location.search);

  const isChildren =
    urlParameters.get("category") === "children" ||
    urlParameters.get("kids") === "true";

  const brandDefined = findBrandByDomain();

  const isMobileSRP = isMobile && brandDefined?.name === "SRP";
  const isSportyAndRich = brandDefined?.name === "Sporty & Rich";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isSRP = brandDefined?.name === "SRP";
  const isPetrone = brandDefined?.name === "Petrone";
  const isNinaRicci = brandDefined?.name === "Nina Ricci";
  const isIntersport = brandDefined?.name === "Intersport";
  const isVelour = brandDefined?.name === "Velour Garments";
  const isPeserico = brandDefined?.name === "Peserico";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const isGivenchyDesktop = isGivenchy && !isMobile;
  const isGivenchyMobile = isGivenchy && isMobile;

  const isSmallHeightDevice = useMediaQuery({
    maxHeight: isGivenchyDesktop ? 850 : 720,
    minWidth: 767,
  });

  const [hovered, setHovered] = useState(false);

  const continueButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (selectedGender) {
      setTimeout(() => {
        continueButtonRef.current?.focus();
      }, 0);
    }
  }, [selectedGender]);

  const handleChangePolicyCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPolicyChecked(event.target.checked);
    handleAnalytics("action", "consent_click", {
      key: "value",
      value: event.target.checked,
    });
  };

  const continueButtonStyles = disableContinue
    ? disabledStylesContinue
    : activeStylesContinue;

  const customLinkStyles = {
    fontSize: policityStyles.fontSize,
    color: welcomeScreenUI.input_fields.title
      .fontColor as React.CSSProperties["color"],
    fontWeight: policityStyles.fontWeight,
    letterSpacing: (policityStyles as any)?.letterSpacing,
  };

  const CTAStyles: React.CSSProperties = {
    ...continueButtonStyles,
    ...(hovered && !disableContinue ? hoverStylesContinue : {}),
    textTransform:
      continueButtonStyles?.textTransform === "capitalize"
        ? "none"
        : continueButtonStyles?.textTransform,
    marginBottom:
      isTDF && isMobile
        ? "20px"
        : isGivenchyDesktop
          ? "122px"
          : isMobile && isVB
            ? "30px"
            : isMobileSRP
              ? "20px"
              : !isMobile
                ? "80px"
                : "10px",
    marginTop: isGivenchyMobile ? "20px" : 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    top: isGivenchyDesktop && isSmallHeightDevice ? "40px" : "",
    position: isGivenchyDesktop && !isSmallHeightDevice ? "fixed" : "relative",
    bottom: isMobileSRP
      ? "22px"
      : isGivenchyDesktop && !isSmallHeightDevice
        ? "30px"
        : 0,
    width:
      isGivenchyDesktop && !isSmallHeightDevice ? "calc(100% - 160px)" : "auto",
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered && !disableContinue ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered && !disableContinue ? 1 : 0,
    transform:
      hovered && !disableContinue ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  const genderMaleKey = isChildren ? "gender.children.male" : "gender.male";

  const genderFemaleKey = isChildren
    ? "gender.children.female"
    : "gender.female";

  const translatedGender =
    selectedGender === GENDERS.M ? t(genderMaleKey) : t(genderFemaleKey);

  const isShowGenderBlock =
    !isMobile ||
    (!isVelour && step?.number === MODAL_STEPS.GENDER.number) ||
    (isVelour && step?.number === MODAL_STEPS.INTRO_MOBILE.number) ||
    isTDF;

  const gapStyles = (() => {
    if (isTDF && !isMobile) return "5px";
    if (isTDF && isMobile) return "5px";

    const shouldUse5px =
      (isMediumHeightRelative && !isSportyAndRich) ||
      (isSmallHeightRelative && isSportyAndRich) ||
      isPetrone ||
      isGivenchy;

    if (shouldUse5px) return "5px";

    return "15px";
  })();

  return (
    <>
      <DescriptionBlock
        step={step}
        selectedGender={selectedGender}
        translatedGender={translatedGender}
        descriptionStyles={descriptionStyles}
        titlesStyles={titlesStyles}
      />
      <form
        style={{
          marginTop: isGivenchyMobile
            ? "10px"
            : isVelour
              ? "40px"
              : isMobile && !isGivenchy
                ? "30px"
                : 0,
          gap: gapStyles,
        }}
      >
        {(!isMobile ||
          (isMobile && step?.number === MODAL_STEPS.INTRO_MOBILE.number)) && (
          <UserInfoBlock
            error={error}
            height={height}
            unit={unit}
            feet={feet}
            inches={inches}
            unitWeight={unitWeight}
            weight={weight}
            age={age}
            handleFieldChange={handleFieldChange}
            handleUnitChange={handleUnitChange}
            handleUnitWeightChange={handleUnitWeightChange}
            setNeedValidate={setNeedValidate}
            titlesStyles={titlesStyles}
            disableContinue={disableContinue}
            nextStep={nextStep}
            ageChildrenYears={ageChildrenYears}
            ageChildrenMonths={ageChildrenMonths}
            unitAge={unitAge}
            handleUnitAgeChange={handleUnitAgeChange}
            company={company}
            setTouchedCompany={setTouchedCompany}
            setFeetTouched={setFeetTouched}
            setInchesTouched={setInchesTouched}
          />
        )}
        {isNinaRicci ? (
          <div
            style={{
              display: "flex",
              gap: "5px",
            }}
          >
            <CustomCheckbox
              checked={policyChecked}
              onChange={handleChangePolicyCheck}
              style={{
                color: "#000000",
                margin: 0,
                padding: 0,
              }}
            />
            <p
              style={customLinkStyles}
              dangerouslySetInnerHTML={{
                __html: t("pp_link")
                  .replace(/<a(.*?)>/g, (match: any) => {
                    return `<a style="${Object.entries(customLinkStyles)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join("; ")}" target="_blank"${match.slice(2)}`;
                  })
                  .replace(/<\/a>\./g, ".</a>"),
              }}
            />
          </div>
        ) : null}
        <div
          className="gender-block"
          style={{
            marginBottom: isGivenchyMobile
              ? "0"
              : isMobile && isSRP && step?.number === MODAL_STEPS.GENDER.number
                ? "20px"
                : "0",
            gap: isGivenchy || isTDF ? "5px" : "20px",
          }}
        >
          {!uxGender && (
            <>
              {!isMobile || isTDF ? (
                <label
                  htmlFor="gender"
                  style={{
                    ...titlesStyles,
                    textTransform:
                      titlesStyles.textTransform === "capitalize"
                        ? "none"
                        : (titlesStyles.textTransform as React.CSSProperties["textTransform"]),
                    marginTop:
                      isTDF && isMobile
                        ? "20px"
                        : isTDF && !isMobile
                          ? "10px"
                          : isGivenchyDesktop
                            ? "30px"
                            : 0,
                    display: "flex",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: isGivenchy
                      ? t("gender.title", {
                          value: selectedGender
                            ? capitalizeFirstLetter(translatedGender)
                            : t("Select"),
                        })
                      : t("gender.title"),
                  }}
                />
              ) : null}
              {isShowGenderBlock && (
                <GenderBlock
                  selectedGender={selectedGender}
                  handleGenderSelect={handleGenderSelect}
                  nextStep={nextStepGender}
                  genderMaleKey={genderMaleKey}
                  genderFemaleKey={genderFemaleKey}
                />
              )}
            </>
          )}
        </div>

        {isGivenchyMobile ||
        !(isMobile && step?.number === MODAL_STEPS.GENDER.number) ? (
          <button
            tabIndex={0}
            ref={continueButtonRef}
            type="button"
            disabled={disableContinue}
            className="continue-button"
            onMouseEnter={() => {
              if (isMobile) return;
              if (!disableContinue) setHovered(true);
            }}
            onMouseLeave={() => {
              if (!disableContinue) setHovered(false);
            }}
            onClick={() => {
              if (disableContinue) return;

              const isGivenchyMobileGender =
                isGivenchyMobile && step?.number === MODAL_STEPS.GENDER.number;

              if (isGivenchyMobileGender) {
                nextStepGender();
              } else {
                nextStep();
                handleAnalytics("action", "info_validation", null);
              }
            }}
            style={CTAStyles}
          >
            <div
              style={{ display: "inline-flex", alignItems: "center", gap: 0 }}
            >
              {continueButtonStyles?.textTransform === "capitalize"
                ? capitalizeFirstLetter(t("continue"))
                : t("continue")}

              {isIntersport && (
                <span style={arrowContainerStyle} aria-hidden>
                  <ChevronRight style={iconStyle} />
                </span>
              )}
            </div>
          </button>
        ) : null}
      </form>
      <MeasuredBy step={step} />
    </>
  );
};

export default StepIntroDesktop;
